import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { endpoints } from "@/api";
import { Card, Button, SeverityPill, fmt$, fmtN, dotColor } from "@/components/primitives";
import RingGauge from "@/components/RingGauge";

const RECENT_KEY = "c4b_recent_snapshots";
function readRecent() { try { return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]"); } catch (e) { return []; } }
function pushRecent(id, name) {
  try {
    const prev = readRecent().filter((r) => r.id !== id);
    localStorage.setItem(RECENT_KEY, JSON.stringify([{ id, name }, ...prev].slice(0, 6)));
  } catch (e) { /* ignore */ }
}

const MIX_COLOR = { beans: "var(--ac-indigo)", chocolate: "var(--ac-cyan)", cleaning: "var(--sv-watch)", powder: "var(--sv-risk)", milk: "var(--sv-good)", water: "var(--ac-cyan)", other: "var(--ui-text-lo)" };
const mixColor = (k) => MIX_COLOR[k] || "var(--ui-text-lo)";

function Stat({ label, value, color, first }) {
  return (
    <div style={{ flex: 1, padding: "0 20px", borderLeft: first ? "none" : "1px solid var(--ui-line-soft)" }}>
      <div className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-lo)" }}>{label}</div>
      <div className="num" style={{ fontSize: 22, fontWeight: 500, letterSpacing: "-0.5px", color: color || "var(--ui-text-hi)", marginTop: 6 }}>{value}</div>
    </div>
  );
}

export default function Snapshot() {
  const { id } = useParams();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const { data: search } = useQuery({ queryKey: ["snapSearch", q], queryFn: () => endpoints.snapshotSearch(q), enabled: q.length > 0 });
  const { data } = useQuery({ queryKey: ["snapshot", id], queryFn: () => endpoints.snapshot(id), enabled: !!id });
  const { data: board } = useQuery({ queryKey: ["leakboard", "all"], queryFn: () => endpoints.leakboard("all"), enabled: !id });
  const [recent, setRecent] = useState(() => readRecent());
  useEffect(() => { if (data?.account && id) { pushRecent(id, data.account.name); setRecent(readRecent()); } }, [data, id]);

  const header = (
    <div className="flex" style={{ justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
      <div>
        <div className="num" style={{ fontSize: 30, fontWeight: 700, letterSpacing: "-0.9px", color: "var(--ui-text-hi)" }}>Client Snapshot</div>
        <div className="prose-i" style={{ fontSize: 14, color: "var(--ui-text-lo)", marginTop: 6 }}>Everything you need before a call or site visit — one click, no digging</div>
      </div>
      <div className="flex no-print" style={{ gap: 12, position: "relative" }}>
        <div style={{ position: "relative" }}>
          <input data-testid="snapshot-search" placeholder="Search account…" value={q} onChange={(e) => setQ(e.target.value)}
            className="prose-i" style={{ background: "var(--ui-bg)", border: "1px solid var(--ui-line)", borderRadius: 10, padding: "9px 14px", color: "var(--ui-text-hi)", fontSize: 14, width: 240 }} />
          {q && search?.results?.length > 0 && (
            <div style={{ position: "absolute", top: 44, left: 0, right: 0, background: "var(--ui-card)", border: "1px solid var(--ui-line)", borderRadius: 10, zIndex: 20, maxHeight: 280, overflowY: "auto", boxShadow: "var(--shadow-deep)" }}>
              {search.results.map((r) => (
                <div key={r.id} onClick={() => { nav(`/snapshot/${r.id}`); setQ(""); }} className="prose-i"
                  style={{ padding: "10px 14px", fontSize: 13.5, color: "var(--ui-text-hi)", cursor: "pointer", borderBottom: "1px solid var(--ui-line-soft)" }}>{r.name}</div>
              ))}
            </div>
          )}
        </div>
        <Button variant="secondary" onClick={() => window.print()}>Print / PDF</Button>
        <Button onClick={() => search?.results?.[0] && nav(`/snapshot/${search.results[0].id}`)}>Generate</Button>
      </div>
    </div>
  );

  // DELIBERATE EXCEPTION (do not "fix" to tokens): the print stylesheet uses
  // FIXED hex colours (#fff / #111 etc.), not screen design tokens. Print must
  // render light with fixed ink regardless of the viewer's theme — a screen
  // token here would be the actual bug. Excluded from the hex/design audit.
  const printStyle = (
    <style>{`@media print {
      :root { --ui-bg:#fff; --ui-card:#fff; --ui-card-2:#f6f6f8; --ui-text-hi:#111; --ui-text-mid:#333; --ui-text-lo:#666; --ui-line:#d5d5db; --ui-line-soft:#e9e9ee; --ui-track:#ececf2; --ui-on-accent:#fff; }
      body { background:#fff !important; -webkit-print-color-adjust:exact; print-color-adjust:exact; }
      nav, aside, .app-sidebar, .no-print { display:none !important; }
      .hero-gradient { background:#fff !important; }
      @page { size: A4 portrait; margin: 12mm; }
    }`}</style>
  );

  if (!id) {
    const flagged = (board?.accounts || []).slice(0, 6);
    return (<div>{header}
      <div className="flex" style={{ gap: 20, alignItems: "flex-start" }}>
        <Card style={{ flex: 1, padding: 24 }}>
          <div className="num" style={{ fontSize: 17, fontWeight: 500, color: "var(--ui-text-hi)" }}>Recently viewed</div>
          <div className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-lo)", marginTop: 3, marginBottom: 10 }}>your last few pre-call sheets</div>
          {recent.length === 0
            ? <div className="prose-i" style={{ fontSize: 13, color: "var(--ui-text-lo)", paddingTop: 8 }}>Nothing yet — search above to open an account.</div>
            : recent.map((r) => (
                <div key={r.id} onClick={() => nav(`/snapshot/${r.id}`)} className="prose-i" style={{ fontSize: 13.5, color: "var(--ui-text-hi)", cursor: "pointer", padding: "10px 0", borderTop: "1px solid var(--ui-line-soft)" }}>{r.name}</div>))}
        </Card>
        <Card style={{ flex: 1, padding: 24 }}>
          <div className="num" style={{ fontSize: 17, fontWeight: 500, color: "var(--ui-text-hi)" }}>Most worth calling</div>
          <div className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-lo)", marginTop: 3, marginBottom: 10 }}>flagged accounts, highest annualised exposure first</div>
          {flagged.length === 0
            ? <div className="prose-i" style={{ fontSize: 13, color: "var(--ui-text-lo)", paddingTop: 8 }}>No flagged accounts.</div>
            : flagged.map((r) => (
                <div key={r.id} onClick={() => nav(`/snapshot/${r.customerId}`)} className="flex items-center" style={{ justifyContent: "space-between", cursor: "pointer", padding: "11px 0", borderTop: "1px solid var(--ui-line-soft)" }}>
                  <span className="flex items-center" style={{ gap: 10 }}><SeverityPill severity={r.severity} label={r.typeLabel} /><span className="prose-i" style={{ fontSize: 13.5, color: "var(--ui-text-hi)" }}>{r.name}</span></span>
                  <span className="num" style={{ fontSize: 14, fontWeight: 600, color: "var(--ui-text-hi)" }}>{fmt$(r.valueAtRisk)}</span>
                </div>))}
        </Card>
      </div></div>);
  }
  if (!data) return <div>{header}<div className="prose-i" style={{ color: "var(--ui-text-lo)" }}>Loading…</div></div>;

  const { account, stats, gauge, mix, sites, talkingPoints, openJobNote } = data;
  const initials = account.name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const gaugeStops = ["var(--sv-watch)", "var(--sv-risk)"];

  return (
    <div>{printStyle}{header}
      <div className="flex" style={{ gap: 20, alignItems: "stretch" }}>
        {/* profile */}
        <Card style={{ flex: 1, padding: 26 }}>
          <div className="flex items-center" style={{ gap: 16 }}>
            <div style={{ width: 52, height: 52, borderRadius: 14, background: "var(--ac-indigo-soft)", border: "1px solid var(--ac-indigo)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span className="num" style={{ color: "var(--ac-indigo-hi)", fontSize: 18, fontWeight: 700 }}>{initials}</span>
            </div>
            <div>
              <div className="num" style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.5px", color: "var(--ui-text-hi)" }}>{account.name}</div>
              <div className="flex items-center" style={{ gap: 10, marginTop: 6 }}>
                {account.winBack
                  ? <span className="prose-i" style={{ display: "inline-flex", alignItems: "center", fontSize: 11, fontWeight: 700, letterSpacing: "0.4px", textTransform: "uppercase", color: "var(--sv-watch)", background: "var(--sv-watch-bg)", border: "1px solid var(--sv-watch)", borderRadius: 8, padding: "3px 10px" }}>Win-back</span>
                  : <SeverityPill severity={account.severity} />}
                <span className="prose-i" style={{ fontSize: 13, color: "var(--ui-text-lo)" }}>{account.siteCount} sites · {account.siteNames} · {account.ownership}</span>
              </div>
              {account.winBack && account.winBackReason && (
                <div className="prose-i" style={{ fontSize: 12, color: "var(--sv-watch)", marginTop: 5 }}>Win-back opportunity — {account.winBackReason}</div>
              )}
            </div>
          </div>
          <div className="flex" style={{ marginTop: 24 }}>
            <Stat first label="Spend 12 months" value={fmt$(stats.spend12m)} />
            <Stat label="vs prior year" value={stats.vsPriorPct == null ? (stats.vsPriorLabel || "Insufficient history") : `${stats.vsPriorPct}%`} color={stats.vsPriorPct != null && stats.vsPriorPct < 0 ? "var(--sv-risk)" : "var(--sv-good)"} />
            <Stat label="Contract ends" value={stats.contractEnds} />
          </div>
          <div className="flex" style={{ marginTop: 22 }}>
            <Stat first label="Last order" value={stats.lastOrder} />
            <Stat label="Machines" value={`${stats.machines} units`} />
            <Stat label="Open jobs" value={stats.openJobs > 0 ? `${stats.openJobs} service` : "None"} color={stats.openJobs > 0 ? "var(--sv-watch)" : undefined} />
            <Stat label="Account manager" value={stats.accountManager || account.accountManager || "—"} />
          </div>
        </Card>
        {/* gauge */}
        <Card style={{ width: 320, padding: 26, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          background: "transparent" }} className="hero-gradient">
          <RingGauge size={172} value={gauge.hasBaseline ? gauge.pct : 0} stops={gaugeStops} glow="rgba(255,138,61,.5)" centerNumber={gauge.hasBaseline ? gauge.pct : "—"} label={gauge.hasBaseline ? "% of expected" : "no baseline"} numberSize={gauge.hasBaseline ? 42 : 34} />
          <div className="num" style={{ fontSize: 18, color: "var(--ui-text-hi)", marginTop: 16 }}>{gauge.hasBaseline ? `${fmtN(gauge.actualKg)} / ${fmtN(gauge.expectedKg)} kg` : `${fmtN(gauge.actualKg)} kg this month`}</div>
          <div className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-lo)", marginTop: 4, textAlign: "center", maxWidth: 240 }}>{gauge.hasBaseline ? "this month" : "No baseline — owned or too few orders to set one"}{gauge.daysDry != null ? ` · ${gauge.daysDry} days dry` : ""}</div>
        </Card>
      </div>

      <div className="flex" style={{ gap: 20, marginTop: 20, alignItems: "flex-start" }}>
        {/* consumable mix */}
        <Card style={{ flex: 1, padding: 26 }}>
          <div className="num" style={{ fontSize: 17, fontWeight: 500, color: "var(--ui-text-hi)" }}>Consumable mix</div>
          <div className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-lo)", marginTop: 3, marginBottom: 18 }}>last 12 months by value</div>
          {mix.map((m) => (
            <div key={m.key} style={{ marginBottom: 18 }}>
              <div className="flex items-center" style={{ justifyContent: "space-between", marginBottom: 8 }}>
                <span className="flex items-center prose-i" style={{ gap: 8, fontSize: 13.5, color: "var(--ui-text-hi)" }}>
                  <span style={{ width: 8, height: 8, borderRadius: 9999, background: mixColor(m.key) }} />{m.category}</span>
                <span className="num" style={{ fontSize: 14, color: "var(--ui-text-hi)" }}>{fmt$(m.value)}</span>
              </div>
              <div style={{ height: 6, borderRadius: 4, background: "var(--ui-track)", overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${m.pct}%`, background: mixColor(m.key), borderRadius: 4 }} />
              </div>
            </div>
          ))}
        </Card>
        {/* sites & machines */}
        <Card style={{ flex: 1, padding: 26 }}>
          <div className="num" style={{ fontSize: 17, fontWeight: 500, color: "var(--ui-text-hi)" }}>Sites &amp; machines</div>
          <div className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-lo)", marginTop: 3, marginBottom: 14 }}>{sites.length} sites</div>
          {sites.map((s, i) => (
            <div key={i} className="flex" style={{ gap: 12, padding: "14px 0", borderTop: i === 0 ? "none" : "1px solid var(--ui-line-soft)" }}>
              <span style={{ width: 3, alignSelf: "stretch", background: dotColor(s.severity), borderRadius: 4 }} />
              <div style={{ flex: 1 }}>
                <div className="prose-i" style={{ fontSize: 13.5, fontWeight: 600, color: s.archived ? "var(--ui-text-mid)" : "var(--ui-text-hi)" }}>{s.name}{s.archived ? " · closed / relocated" : (s.linked === false ? " · unlinked in Simpro" : "")}</div>
                {s.machines && s.machines.length > 0
                  ? s.machines.map((m, j) => (<div key={j} className="prose-i" style={{ fontSize: 11, color: "var(--ui-text-lo)", marginTop: 2 }}>{m.machine} · {m.serial}</div>))
                  : <div className="prose-i" style={{ fontSize: 11, color: "var(--ui-text-lo)", marginTop: 2 }}>No machine recorded</div>}
                {s.contacts && s.contacts.length > 0
                  ? s.contacts.map((ct, k) => (
                      <div key={`c${k}`} className="prose-i" style={{ fontSize: 11, color: "var(--ui-text-mid)", marginTop: 3 }}>
                        <span style={{ fontWeight: 600 }}>{ct.name}</span>{ct.phone ? ` · ${ct.phone}` : ""}{ct.email ? ` · ${ct.email}` : ""}
                      </div>))
                  : <div className="prose-i" style={{ fontSize: 11, color: "var(--ui-text-lo)", marginTop: 3, fontStyle: "italic" }}>No contact recorded in Simpro</div>}
              </div>
              <div style={{ textAlign: "right" }}>
                <div className="num" style={{ fontSize: 15, color: dotColor(s.severity) }}>{fmtN(s.kg)} kg</div>
                <div className="prose-i" style={{ fontSize: 10.5, color: "var(--ui-text-lo)" }}>{s.kgWindow || "last 12 months"}</div>
              </div>
            </div>
          ))}
          {openJobNote && (
            <div style={{ background: "var(--sv-watch-bg)", border: "1px solid var(--sv-watch)", borderRadius: 10, padding: "12px 14px", marginTop: 12, position: "relative" }}>
              <span style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 3, background: "var(--sv-watch)" }} />
              <span className="prose-i" style={{ fontSize: 12.5, color: "var(--sv-watch)", paddingLeft: 8 }}>{openJobNote}</span>
            </div>
          )}
        </Card>
        {/* talking points */}
        <Card style={{ flex: 1, padding: 26 }}>
          <div className="num" style={{ fontSize: 17, fontWeight: 500, color: "var(--ui-text-hi)" }}>Talking points</div>
          <div className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-lo)", marginTop: 3, marginBottom: 14 }}>auto-generated from the data</div>
          {talkingPoints.map((t, i) => (
            <div key={i} className="flex" style={{ gap: 12, marginBottom: 18 }}>
              <span style={{ width: 8, height: 8, borderRadius: 9999, background: dotColor(t.dot), marginTop: 5 }} />
              <div>
                <div className="prose-i" style={{ fontSize: 13.5, fontWeight: 600, color: "var(--ui-text-hi)" }}>{t.claim}</div>
                <div className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-mid)", marginTop: 3 }}>{t.detail}</div>
              </div>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}
